import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Alert, ErrorBoundary } from "../../shared/ui";
import { Download, Loader2 } from "lucide-react";
import DynamicFileViewer from "../../components/FileViewers/DynamicFileViewer";
import { getApiError } from "../../api/client";
import { studioDocumentsApi } from "../api/studioApi";

export default function DocumentPreviewModal({ open, onClose, run, title, onDownload, isDownloading }) {
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (!open || !run) return;

    const fetchPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const blob = await studioDocumentsApi.exportPreview(run.id);
        if (active) {
          const url = window.URL.createObjectURL(blob);
          setFileUrl(url);
        }
      } catch (err) {
        console.error("Failed to fetch document preview:", err);
        if (active) {
          setError(getApiError(err));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchPreview();

    return () => {
      active = false;
      if (fileUrl) {
        window.URL.revokeObjectURL(fileUrl);
      }
    };
  }, [open, run?.id]);

  useEffect(() => {
    return () => {
      if (fileUrl) {
        window.URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  if (!run) return null;

  const footer = (
    <div className="flex justify-end w-full gap-3 py-2">
      <Button variant="secondary" onClick={onClose} disabled={isDownloading || loading}>Cancel</Button>
      <Button variant="primary" onClick={onDownload} disabled={isDownloading || loading}>
        {isDownloading ? <><Loader2 size={13} className="animate-spin" /> Downloading...</> : <><Download size={13} /> Download Document</>}
      </Button>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={`Preview: ${title}`} size="lg" footer={footer}>
      <div className="bg-white flex flex-col items-center justify-center min-h-[400px]">
        {loading ? (
          <div className="flex flex-col items-center justify-center text-gray-500 gap-3">
            <Loader2 className="animate-spin" size={32} />
            <p>Generating PDF Preview...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" title="Couldn't generate the preview">{error}</Alert>
        ) : fileUrl ? (
          <ErrorBoundary title="Preview unavailable">
            <DynamicFileViewer fileUrl={fileUrl} filename="preview.pdf" />
          </ErrorBoundary>
        ) : null}
      </div>
    </Modal>
  );
}

DocumentPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  run: PropTypes.object,

  title: PropTypes.string,
  onDownload: PropTypes.func,
  isDownloading: PropTypes.bool
};
